import { OperationRoleEnum } from "../ars_ldm";
import { ARSSelect } from "../inputs/ARSSelect";
import { ARSText } from "../inputs/ARSText";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the ReferencedOperationRelationshipControl component.
 */
export interface ReferencedOperationRelationshipControlProps {
  /** RFC 6901 JSON Pointer path to the ReferencedOperationRelationship in ARSContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
  /** Sibling count */
  siblingCount: number;
}

/**
 * A component for managing ReferencedOperationRelationship entities.
 *
 * This component provides:
 * - Role selection via OperationRoleEnum
 * - Operation and analysis identifier inputs
 * - Description input
 * - Remove button for deleting the relationship
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the ReferencedOperationRelationship in ARSContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @param props.siblingCount - Number of sibling items
 * @returns A React functional component rendering the referenced operation relationship editor.
 */
export const ReferencedOperationRelationshipControl = ({
  dataPath,
  disabled = false,
  siblingCount,
}: ReferencedOperationRelationshipControlProps): React.ReactElement => {
  const roleOptions = Object.values(OperationRoleEnum).map((role) => ({
    value: role,
    label: role,
  }));

  const idRoute = dataPath.replace(/\//g, "-");

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      <div className="position-absolute top-0 end-0 m-2">
        <RemoveArsItemButton
          dataPath={dataPath}
          disabled={disabled}
          siblingCount={siblingCount}
        />
      </div>

      <ARSSelect
        dataPath={`${dataPath}/referencedOperationRole/controlledTerm`}
        id={`${idRoute}-role`}
        label="Role"
        labelWidth={2}
        options={roleOptions}
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/operationId`}
        id={`${idRoute}-operation-id`}
        label="Operation id"
        labelWidth={2}
        placeholder="Enter operation id"
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/analysisId`}
        id={`${idRoute}-analysis-id`}
        label="Analysis id"
        labelWidth={2}
        placeholder="Enter analysis id"
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/description`}
        id={`${idRoute}-description`}
        label="Description"
        labelWidth={2}
        placeholder="Enter description"
        type="textarea"
        debounceMilliseconds={2000}
        rows={3}
        disabled={disabled}
      />
    </div>
  );
};

ReferencedOperationRelationshipControl.displayName = "ReferencedOperationRelationshipControl";
